import classNames from "classnames";
import * as moment from "moment";
import { IKeyValueLog } from "motw_core/Entity/IKeyValueLog";
import { ILogEntry } from "motw_core/Entity/ILogEntry";
import * as React from "react";
import { GetEmotionColor } from "source/app/domain/Emotions/Emotions";
import { IsFirstEntryOfDay } from "source/app/presentation/MainPanel/Log/LogEntries/LogEntries";
import * as styles from "source/app/presentation/MainPanel/Log/LogEntries/LogEntries.css";
import { ILogEntryProps } from "source/app/presentation/MainPanel/Log/LogEntries/LogEntryProps";
import LogEntrySymbol from "source/app/presentation/MainPanel/Log/LogEntries/LogEntrySymbol/LogEntrySymbol";
import { DisplayLogEntryText } from "source/app/presentation/MainPanel/Log/LogEntries/Row/EntryText/DisplayLogEntryText";
import Maths from "source/app/services/Maths";
import { IsToday } from "source/app/services/Time/Time";
import UI from "source/app/services/UI/UI";
import { StyleClassAuto } from "source/app/presentation/Shared/Styles/Auto/StyleClassAuto";

export function LogEntryRow({
    data,
    row,
    HandleDataChanged,
    keyValueLogs,
}: { row: ILogEntry; keyValueLogs: IKeyValueLog[] } & ILogEntryProps) {
    const shouldReduceRowOpacity = !IsToday(row.lastmodifiedtime);
    const isMonumentalEntry = UI.GetEntrySignificance(row.entry) > 2;
    const backgroundColor = UI.GetLogEntryBackgroundColor(
        row.entry,
        isMonumentalEntry ? 0.18 : 0.105
    );
    const borderColor = UI.GetLogEntryBackgroundColor(
        row.entry,
        isMonumentalEntry ? 0.46 : 0.22
    );
    const hoverBackgroundColor = UI.GetLogEntryBackgroundColor(
        row.entry,
        isMonumentalEntry ? 0.24 : 0.145
    );
    const fallbackRailAccentColor = UI.GetDriveColorFromSymbol(
        UI.GetEventCategory(row.entry)
    );
    const railAccentColor = GetRailAccentColor(
        row,
        keyValueLogs,
        fallbackRailAccentColor
    );
    const mutedRailAccentColor = Maths.ConvertHexToRgbA(railAccentColor, 0.22);
    const hoverMutedRailAccentColor = Maths.ConvertHexToRgbA(
        railAccentColor,
        0.3
    );

    return (
        <>
            <div
                className={classNames(
                    "logItem",
                    styles.logRow,
                    {
                        [styles.reduceOpacity]: shouldReduceRowOpacity,
                    }
                )}
                style={GetRowStyleVariables(
                    backgroundColor,
                    borderColor,
                    hoverBackgroundColor,
                    mutedRailAccentColor,
                    hoverMutedRailAccentColor
                )}
                data-row={row.id}
                key={row.id}
            >
                <div
                    className={StyleClassAuto({
                        alignItems: `center`,
                        display: `grid`,
                        gridTemplateColumns: `68px minmax(0, 1fr) 44px`,
                        minHeight: `32px`,
                        minWidth: 0,
                        position: `relative`,
                        wordWrap: `break-word`,
                        zIndex: 1,
                    })}
                >
                    <span
                        className={StyleClassAuto({
                            color: `rgb(214 207 192 / 0.92)`,
                            alignItems: `center`,
                            borderRight: `1px solid rgba(180, 218, 239, 0.28)`,
                            display: `flex`,
                            fontVariantNumeric: `tabular-nums`,
                            height: `100%`,
                            justifyContent: `center`,
                            letterSpacing: `0.04em`,
                            textAlign: `center`,
                            textShadow: `0 1px 2px rgba(0, 0, 0, 0.5)`,
                        })}
                    >
                        {moment(row.lastmodifiedtime).format(`HH:mm`)}
                    </span>
                    {DisplayLogEntryText(row)}
                    <span
                        className={StyleClassAuto({
                            alignItems: `center`,
                            alignSelf: `stretch`,
                            background: `transparent`,
                            borderLeft: `1px solid rgba(255, 255, 255, 0.12)`,
                            display: `flex`,
                            justifyContent: `center`,
                            boxShadow: `inset 1px 0 0 rgba(255, 255, 255, 0.06)`,
                            width: `44px`,
                            transition: `background 500ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 500ms cubic-bezier(0.2, 0.8, 0.2, 1)`,
                        })}
                    >
                        <LogEntrySymbol
                            entry={row.entry}
                            rowId={row.id}
                            goalDatapointId={row.goaldatapointid}
                            noRightMargin
                        />
                    </span>
                </div>
            </div>
            {IsFirstEntryOfDay(data, row) && <DayDivider />}
        </>
    );
}

function GetRailAccentColor(
    row: ILogEntry,
    keyValueLogs: IKeyValueLog[],
    fallbackColor: string
) {
    if (keyValueLogs.length === 0) {
        return fallbackColor;
    }

    const rowMoment = moment(row.lastmodifiedtime);
    const closestKeyValueLog = keyValueLogs
        .map((kvl) => ({
            timeDifference: rowMoment.diff(
                moment(kvl.lastmodifiedtime),
                "minutes"
            ),
            keyValueLog: kvl,
        }))
        .filter((tuple) => tuple.timeDifference > 0)
        .sort((a, b) => a.timeDifference - b.timeDifference)[0]?.keyValueLog;

    return closestKeyValueLog
        ? GetEmotionColor(closestKeyValueLog.value)
        : fallbackColor;
}

function DayDivider() {
    return (
        <div
            className={StyleClassAuto({
                background: `linear-gradient(90deg, rgb(245 178 38 / 0) 0%, rgb(245 178 38 / 0.72) 18%, rgb(245 178 38 / 0.94) 50%, rgb(245 178 38 / 0.72) 82%, rgb(245 178 38 / 0) 100%)`,
                height: `1px`,
                margin: `7px 0`,
            })}
        />
    );
}

function GetRowStyleVariables(
    backgroundColor: string,
    borderColor: string,
    hoverBackgroundColor: string,
    railAccentColor: string,
    hoverRailAccentColor: string
) {
    return {
        "--log-row-background-color": backgroundColor,
        "--log-row-border-color": borderColor,
        "--log-row-hover-background-color": hoverBackgroundColor,
        "--log-row-rail-accent-color": railAccentColor,
        "--log-row-hover-rail-accent-color": hoverRailAccentColor,
    } as React.CSSProperties;
}
